import { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { ladeSpielerNamen, spielerName } from '../utils/spielerNamen';
import { useAuth } from '../contexts/AuthContext';
import { LIFETIME_BADGES, SAISON_BADGES } from '../data/achievementDefinitions';
import { saisonIdZuName } from '../utils/saison';
import './HallOfFame.css';

// Hall of Fame: Champions je Saison (Podest), Medaillenspiegel, Achievements.
// Liest hallOfFame (wenige Docs) und – erst beim Öffnen des Reiters –
// achievements + users. Berechnet nichts selbst: Achievements erzeugt der
// Admin über das Dashboard.

const MEDAILLE = { 1: '🥇', 2: '🥈', 3: '🥉' };

// Alte Docs (platz1/2/3) auf das neue Format (top3Plaetze) bringen
function normalisiereHof(id, data) {
  let top3 = data.top3Plaetze;
  if (!top3 && data.platz1) {
    top3 = [1, 2, 3]
      .filter(p => data[`platz${p}`])
      .map(p => ({ platz: p, spieler: [data[`platz${p}`]] }));
  }
  if (!top3 || top3.length === 0) return null;
  // abgeschlossenAm: ISO-String, Firestore-Timestamp oder etwas Unlesbares
  let abgeschlossen = null;
  const roh = data.abgeschlossenAm;
  if (roh?.toDate) abgeschlossen = roh.toDate();
  else if (roh) {
    const d = new Date(roh);
    if (!Number.isNaN(d.getTime())) abgeschlossen = d;
  }
  return {
    saisonId: id,
    saison: data.saison || saisonIdZuName(id),
    top3Plaetze: top3,
    alleRaenge: data.alleRaenge || [],
    teilnehmerAnzahl: data.teilnehmerAnzahl || data.alleRaenge?.length || 0,
    abgeschlossenAm: abgeschlossen
  };
}

function HallOfFame() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;

  const [saisons, setSaisons] = useState([]);
  const [laden, setLaden] = useState(true);
  const [tab, setTab] = useState('champions');

  const [achievements, setAchievements] = useState(null);
  const [achievementsLaden, setAchievementsLaden] = useState(false);
  const [achTab, setAchTab] = useState('gesamt');
  const [badgeSaison, setBadgeSaison] = useState(null);

  useEffect(() => {
    let abgebrochen = false;
    getDocs(collection(db, 'hallOfFame')).then(snap => {
      if (abgebrochen) return;
      const liste = snap.docs.map(d => normalisiereHof(d.id, d.data())).filter(Boolean)
        .sort((a, b) => parseInt(b.saison, 10) - parseInt(a.saison, 10));
      setSaisons(liste);
    }).catch(e => console.error('Hall of Fame konnte nicht geladen werden:', e))
      .finally(() => { if (!abgebrochen) setLaden(false); });
    return () => { abgebrochen = true; };
  }, []);

  // Achievements erst beim Öffnen des Reiters (einmal pro Seitenaufruf)
  const achievementsAngefordert = useRef(false);
  useEffect(() => {
    if (tab !== 'achievements' || achievementsAngefordert.current) return;
    achievementsAngefordert.current = true;
    setAchievementsLaden(true);
    (async () => {
      try {
        const [snap, namen] = await Promise.all([getDocs(collection(db, 'achievements')), ladeSpielerNamen()]);
        const liste = snap.docs.map((d) => ({
          userId: d.id,
          username: spielerName(namen, d.id),
          punkte: d.data().punkte || 0,
          lifetime: d.data().lifetime || {},
          saison: d.data().saison || {}
        })).sort((a, b) => b.punkte - a.punkte);
        setAchievements(liste);
      } catch (e) {
        console.error('Achievements konnten nicht geladen werden:', e);
        setAchievements([]);
      } finally {
        setAchievementsLaden(false);
      }
    })();
  }, [tab]);

  const medaillen = useMemo(() => {
    const m = {};
    saisons.forEach(s => s.top3Plaetze.forEach(pe => pe.spieler.forEach(sp => {
      const key = sp.username || sp.userId || '?';
      if (!m[key]) m[key] = { name: key, userId: sp.userId, gold: 0, silber: 0, bronze: 0, gesamt: 0 };
      if (pe.platz === 1) m[key].gold++;
      if (pe.platz === 2) m[key].silber++;
      if (pe.platz === 3) m[key].bronze++;
      m[key].gesamt++;
    })));
    const liste = Object.values(m).sort((a, b) => b.gold - a.gold || b.silber - a.silber || b.bronze - a.bronze);
    let rang = 1;
    return liste.map((e, i) => {
      const v = liste[i - 1];
      if (i > 0 && (v.gold !== e.gold || v.silber !== e.silber || v.bronze !== e.bronze)) rang = i + 1;
      return { ...e, rang };
    });
  }, [saisons]);

  if (laden) {
    return (
      <div className="seite hof-seite">
        <h1>🏛️ Hall of Fame</h1>
        <div className="karte karte--leer">⏳ Lade…</div>
      </div>
    );
  }

  return (
    <div className="seite hof-seite">
      <h1>🏛️ Hall of Fame</h1>
      <p className="seite__untertitel">Die ewigen Champions unseres Tippspiels.</p>

      <div className="reiter" role="tablist">
        <button type="button" role="tab" className={`reiter__tab${tab === 'champions' ? ' ist-aktiv' : ''}`} onClick={() => setTab('champions')}>🏆 Champions</button>
        <button type="button" role="tab" className={`reiter__tab${tab === 'medaillen' ? ' ist-aktiv' : ''}`} onClick={() => setTab('medaillen')}>🥇 Medaillen</button>
        <button type="button" role="tab" className={`reiter__tab${tab === 'achievements' ? ' ist-aktiv' : ''}`} onClick={() => setTab('achievements')}>🏅 Achievements</button>
      </div>

      {tab === 'champions' && (
        saisons.length === 0
          ? <div className="karte karte--leer">Noch keine abgeschlossene Saison. Sobald eine Saison beendet ist, steht hier das Podest.</div>
          : saisons.map(s => <SaisonKarte key={s.saisonId} saison={s} uid={uid} />)
      )}

      {tab === 'medaillen' && (
        <div className="karte">
          <h2>🥇 Medaillenspiegel</h2>
          <p className="karte__hinweis">Podestplätze über alle Saisons, sortiert nach Gold, Silber, Bronze.</p>
          {medaillen.length === 0 ? <p className="karte__hinweis">Noch keine Medaillen vergeben.</p> : (
            <table className="rang-tabelle">
              <thead>
                <tr>
                  <th className="spalte-rang">#</th>
                  <th>Spieler</th>
                  <th className="spalte-zahl">🥇</th>
                  <th className="spalte-zahl">🥈</th>
                  <th className="spalte-zahl">🥉</th>
                  <th className="spalte-zahl spalte-nebenwert">Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {medaillen.map(m => (
                  <tr key={m.name} className={m.userId === uid ? 'ist-ich' : ''}>
                    <td className="spalte-rang">{m.rang === 1 ? '👑' : m.rang}</td>
                    <td>{m.name}</td>
                    <td className="spalte-zahl">{m.gold || '–'}</td>
                    <td className="spalte-zahl">{m.silber || '–'}</td>
                    <td className="spalte-zahl">{m.bronze || '–'}</td>
                    <td className="spalte-zahl spalte-nebenwert">{m.gesamt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'achievements' && (
        achievementsLaden || achievements === null
          ? <div className="karte karte--leer">⏳ Lade Achievements…</div>
          : achievements.length === 0
            ? <div className="karte karte--leer">Noch keine Achievements berechnet. Der Admin stößt die Berechnung im Dashboard an.</div>
            : <Achievements daten={achievements} uid={uid} achTab={achTab} setAchTab={setAchTab} badgeSaison={badgeSaison} setBadgeSaison={setBadgeSaison} />
      )}
    </div>
  );
}

// ---------- Champions ----------

function SaisonKarte({ saison, uid }) {
  const [alle, setAlle] = useState(false);
  const platz = (p) => saison.top3Plaetze.find(e => e.platz === p);
  const hatAlle = saison.alleRaenge.length > 0;

  return (
    <div className="karte hof-saison">
      <div className="hof-saison__kopf">
        <h2>Saison {saison.saison}</h2>
        <span className="hof-saison__meta">
          {saison.teilnehmerAnzahl} Teilnehmer
          {saison.abgeschlossenAm && ` · abgeschlossen ${saison.abgeschlossenAm.toLocaleDateString('de-DE')}`}
        </span>
      </div>

      <div className="podest">
        {[2, 1, 3].map(p => {
          const eintrag = platz(p);
          return (
            <div key={p} className={`podest__platz podest__platz--${p}`}>
              <div className="podest__namen">
                {eintrag ? eintrag.spieler.map((sp, i) => (
                  <div key={sp.userId || i} className={`podest__name${sp.userId === uid ? ' ist-ich' : ''}`}>
                    {sp.username}
                    <span className="podest__punkte">{Number(sp.punkte || 0).toFixed(1)} P</span>
                  </div>
                )) : <div className="podest__name podest__name--leer">–</div>}
              </div>
              <div className="podest__sockel">
                <span className="podest__medaille">{MEDAILLE[p]}</span>
                <span className="podest__nummer">{p}</span>
              </div>
            </div>
          );
        })}
      </div>

      {hatAlle && (
        <>
          <button type="button" className="chip hof-saison__mehr" onClick={() => setAlle(a => !a)}>
            {alle ? 'Platzierungen ausblenden' : `Alle ${saison.alleRaenge.length} Platzierungen`}
          </button>
          {alle && (
            <table className="rang-tabelle" style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th className="spalte-rang">#</th>
                  <th>Spieler</th>
                  <th className="spalte-zahl spalte-nebenwert">Spiele</th>
                  <th className="spalte-zahl spalte-nebenwert">Bonus</th>
                  <th className="spalte-zahl">Punkte</th>
                </tr>
              </thead>
              <tbody>
                {[...saison.alleRaenge].sort((a, b) => (a.platz || 0) - (b.platz || 0)).map((r, i) => (
                  <tr key={r.userId || i} className={r.userId === uid ? 'ist-ich' : ''}>
                    <td className="spalte-rang">{MEDAILLE[r.platz] || r.platz}</td>
                    <td>{r.username}</td>
                    <td className="spalte-zahl spalte-nebenwert">{Number(r.gesamtSpiele || 0).toFixed(1)}</td>
                    <td className="spalte-zahl spalte-nebenwert">{Number(r.gesamtBonus || 0).toFixed(1)}</td>
                    <td className="spalte-zahl">{Number(r.punkte || 0).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Achievements ----------

function Achievements({ daten, uid, achTab, setAchTab, badgeSaison, setBadgeSaison }) {
  const saisonIds = useMemo(() => {
    const set = new Set();
    daten.forEach(u => Object.keys(u.saison || {}).forEach(s => set.add(s)));
    return Array.from(set).sort().reverse();
  }, [daten]);
  const aktiveSaison = badgeSaison || saisonIds[0] || null;

  return (
    <>
      <div className="chips" role="tablist">
        <button type="button" className={`chip${achTab === 'gesamt' ? ' ist-aktiv' : ''}`} onClick={() => setAchTab('gesamt')}>🎉 Gesamt</button>
        <button type="button" className={`chip${achTab === 'saison' ? ' ist-aktiv' : ''}`} onClick={() => setAchTab('saison')}>🏅 Saison-Badges</button>
        {Object.entries(LIFETIME_BADGES).map(([key, b]) => (
          <button key={key} type="button" className={`chip${achTab === key ? ' ist-aktiv' : ''}`} onClick={() => setAchTab(key)}>{b.emoji} {b.name}</button>
        ))}
      </div>

      {achTab === 'gesamt' && (
        <div className="karte">
          <h2>🎉 Achievement-Ranking</h2>
          <p className="karte__hinweis">Punkte aus Lifetime- und Saison-Badges, unabhängig von den Tipp-Punkten.</p>
          <table className="rang-tabelle">
            <thead>
              <tr><th className="spalte-rang">#</th><th>Spieler</th><th className="spalte-zahl">Badges</th><th className="spalte-zahl">Punkte</th></tr>
            </thead>
            <tbody>
              {(() => { let rang = 1; return daten.map((u, i) => {
                if (i > 0 && daten[i - 1].punkte !== u.punkte) rang = i + 1;
                const badges = Object.values(u.lifetime).filter(b => b?.stufe).length +
                  Object.values(u.saison).reduce((n, s) => n + Object.values(s || {}).filter(b => b?.unlocked).length, 0);
                return (
                  <tr key={u.userId} className={u.userId === uid ? 'ist-ich' : ''}>
                    <td className="spalte-rang">{rang === 1 ? '👑' : rang}</td>
                    <td>{u.username}</td>
                    <td className="spalte-zahl">🏆 {badges}</td>
                    <td className="spalte-zahl">{u.punkte}</td>
                  </tr>
                );
              }); })()}
            </tbody>
          </table>
        </div>
      )}

      {achTab === 'saison' && (
        saisonIds.length === 0 ? <div className="karte karte--leer">Noch keine Saison-Badges vergeben.</div> : (
          <>
            <div className="werkzeuge" style={{ marginBottom: 12 }}>
              <select className="werkzeuge__select" value={aktiveSaison || ''} onChange={e => setBadgeSaison(e.target.value)} aria-label="Saison">
                {saisonIds.map(s => <option key={s} value={s}>Saison {saisonIdZuName(s)}</option>)}
              </select>
            </div>
            <div className="karten-grid">
              {Object.entries(SAISON_BADGES).map(([key, badge]) => {
                const traeger = daten
                  .map(u => ({ u, b: u.saison?.[aktiveSaison]?.[key] }))
                  .filter(x => x.b?.unlocked)
                  .sort((x, y) => {
                    if (badge.stufen) {
                      const o = badge.stufen.map(s => s.id);
                      const d = o.indexOf(y.b.stufe) - o.indexOf(x.b.stufe);
                      if (d !== 0) return d;
                    }
                    return (y.b.anzahl || y.b.streak || 0) - (x.b.anzahl || x.b.streak || 0);
                  });
                return (
                  <div key={key} className="karte badge-karte">
                    <div className="badge-karte__kopf">
                      <span className="badge-karte__emoji">{badge.emoji}</span>
                      <div>
                        <div className="badge-karte__name">{badge.name}</div>
                        <div className="badge-karte__text">{badge.beschreibung}</div>
                      </div>
                    </div>
                    {traeger.length === 0 ? <div className="badge-karte__leer">Noch niemand</div> : (
                      <div className="liste">
                        {traeger.map(({ u, b }, i) => (
                          <div key={u.userId} className={`liste__zeile${i === 0 ? ' liste__zeile--top' : ''}`}>
                            <span className={u.userId === uid ? 'ist-ich-text' : ''}>{MEDAILLE[i + 1] || ''} {u.username}</span>
                            <span className="liste__wert">
                              {badge.stufen && b.stufe && `${badge.stufen.find(s => s.id === b.stufe)?.emoji || ''} `}
                              {b.anzahl ? `${b.anzahl}×` : b.streak ? `${b.streak} Streak` : '✓'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )
      )}

      {achTab !== 'gesamt' && achTab !== 'saison' && LIFETIME_BADGES[achTab] && (
        <BadgeDetail badge={LIFETIME_BADGES[achTab]} badgeKey={achTab} daten={daten} uid={uid} />
      )}
    </>
  );
}

function BadgeDetail({ badge, badgeKey, daten, uid }) {
  const stufenOrder = badge.stufen.map(s => s.id);
  const traeger = daten
    .filter(u => u.lifetime?.[badgeKey]?.stufe)
    .map(u => ({ ...u, b: u.lifetime[badgeKey], stufeDef: badge.stufen.find(s => s.id === u.lifetime[badgeKey].stufe) }))
    .sort((a, b) => stufenOrder.indexOf(b.b.stufe) - stufenOrder.indexOf(a.b.stufe) || (b.b.wert || 0) - (a.b.wert || 0));

  return (
    <div className="karte">
      <div className="badge-karte__kopf">
        <span className="badge-karte__emoji badge-karte__emoji--gross">{badge.emoji}</span>
        <div>
          <h2 style={{ margin: 0 }}>{badge.name}</h2>
          <div className="badge-karte__text">{badge.beschreibung}</div>
        </div>
      </div>

      <div className="stufen">
        {badge.stufen.map(s => (
          <span key={s.id} className="stufe" title={s.beschreibung || (s.wert !== undefined ? `ab ${s.wert}` : '')}>
            {s.emoji} {s.name}{s.wert !== undefined && <span className="stufe__wert"> · {s.wert}</span>}
          </span>
        ))}
      </div>

      {traeger.length === 0 ? (
        <p className="karte__hinweis">Noch niemand hat dieses Badge. Sei der Erste!</p>
      ) : (
        <table className="rang-tabelle">
          <thead>
            <tr><th className="spalte-rang">#</th><th>Spieler</th><th>Stufe</th><th className="spalte-zahl">Wert</th></tr>
          </thead>
          <tbody>
            {traeger.map((u, i) => (
              <tr key={u.userId} className={u.userId === uid ? 'ist-ich' : ''}>
                <td className="spalte-rang">{MEDAILLE[i + 1] || i + 1}</td>
                <td>{u.username}</td>
                <td>{u.stufeDef?.emoji} {u.stufeDef?.name}</td>
                <td className="spalte-zahl">{typeof u.b.wert === 'number' ? Math.round(u.b.wert * 10) / 10 : (u.b.wert ?? '–')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default HallOfFame;
